import React from "react";
import { decimalsFor } from "../utils/instrument";
import { useApi } from "../hooks/useApi";

const card = { background: "#0d1224", border: "1px solid #1e2a45", borderRadius: "10px", padding: "16px" };
const tbl  = { width: "100%", borderCollapse: "collapse", fontSize: "12px" };
const th   = { padding: "6px 8px", textAlign: "left", color: "#475569", borderBottom: "1px solid #1e2a45", fontWeight: 500 };
const td   = { padding: "6px 8px", borderBottom: "1px solid #0f172a" };

export default function LivePositions() {
  const { data, loading } = useApi("/positions", 5000);

  const positions = data?.positions || [];
  const account   = data?.account   || {};
  const totalPnl  = positions.reduce((s, p) => s + (p.profit || 0), 0);

  return (
    <div style={card}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px" }}>
        <span style={{ fontWeight: 600, color: "#94a3b8" }}>📂 Open Positions ({positions.length})</span>
        <span style={{ fontSize: "12px", color: "#64748b" }}>
          Equity: <b style={{ color: "#60a5fa" }}>${account.equity?.toFixed(2) ?? "—"}</b>
          {" | "}Balance: <b style={{ color: "#94a3b8" }}>${account.balance?.toFixed(2) ?? "—"}</b>
        </span>
      </div>

      {loading && <div style={{ color: "#475569", fontSize: "12px", padding: "20px 0" }}>Loading positions...</div>}

      {!loading && positions.length === 0 && (
        <div style={{ color: "#334155", textAlign: "center", padding: "40px 0", fontSize: "13px" }}>
          No open positions
        </div>
      )}

      {positions.length > 0 && (
        <>
          <div className="sentinel-table-wrap">
            <table style={tbl}>
              <thead>
                <tr>
                  {["Symbol","Type","Lots","Entry","Current","SL","TP","P&L","R","Peak","Conf","Risk $","Age","Status","Tranche","Ticket"].map((h) => (
                    <th key={h} style={th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {positions.map((p) => {
                  // v5.16: per-instrument, not a JPY special case — US500 quotes to 2dp
                  const dp = decimalsFor(p.symbol, p.current_price ?? p.price_current);
                  const pnlColor = p.profit > 0 ? "#4ade80" : p.profit < 0 ? "#f87171" : "#94a3b8";
                  const entry = p.open_price ?? p.price_open;
                  const cur = p.current_price ?? p.price_current;
                  const ageWarn = p.age_mins != null && p.max_holding_mins && p.age_mins > 0.75 * p.max_holding_mins;
                  const fmtAge = (m) => m == null ? "—" : `${Math.floor(m / 60)}h${String(m % 60).padStart(2, "0")}`;
                  return (
                    <tr key={p.ticket}>
                      <td style={td}><b style={{ color: "#e2e8f0" }}>{p.symbol}</b></td>
                      <td style={td}><span style={{ color: p.type === "BUY" ? "#4ade80" : "#f87171", fontWeight: 600 }}>{p.type}</span></td>
                      <td style={td}>{p.volume}</td>
                      <td style={td}>{entry?.toFixed(dp) ?? "—"}</td>
                      <td style={td}>{cur?.toFixed(dp) ?? "—"}</td>
                      <td style={{ ...td, color: "#f87171" }} title={p.sl_dist_pips != null ? `${p.sl_dist_pips} pips away` : ""}>
                        {p.sl?.toFixed(dp) || "—"}
                        {p.sl_dist_pips != null && <span style={{ color: "#475569", fontSize: "10px" }}> ({p.sl_dist_pips}p)</span>}
                      </td>
                      <td style={{ ...td, color: "#4ade80" }} title={p.tp_dist_pips != null ? `${p.tp_dist_pips} pips away` : ""}>
                        {p.tp?.toFixed(dp) || "—"}
                        {p.tp_dist_pips != null && <span style={{ color: "#475569", fontSize: "10px" }}> ({p.tp_dist_pips}p)</span>}
                      </td>
                      <td style={{ ...td, color: pnlColor, fontWeight: 600 }}>
                        {p.profit >= 0 ? "+" : ""}${p.profit?.toFixed(2)}
                      </td>
                      <td style={{ ...td, color: p.r_multiple > 0 ? "#4ade80" : p.r_multiple < 0 ? "#f87171" : "#94a3b8", fontWeight: 600 }}>
                        {p.r_multiple != null ? `${p.r_multiple > 0 ? "+" : ""}${p.r_multiple.toFixed(2)}R` : "—"}
                      </td>
                      <td style={{ ...td, color: "#94a3b8" }}>
                        {p.peak_profit != null && p.peak_profit > 0 ? `$${p.peak_profit.toFixed(2)}` : "—"}
                      </td>
                      <td style={{ ...td, color: p.ai_confidence >= 0.65 ? "#4ade80" : "#94a3b8" }}>
                        {p.ai_confidence != null ? `${(p.ai_confidence * 100).toFixed(0)}%` : "—"}
                      </td>
                      <td style={td}>{p.initial_risk != null ? `$${p.initial_risk.toFixed(0)}` : "—"}</td>
                      <td style={{ ...td, color: ageWarn ? "#fbbf24" : "#94a3b8" }}
                          title={p.max_holding_mins ? `max ${Math.floor(p.max_holding_mins / 60)}h` : ""}>
                        {fmtAge(p.age_mins)}{p.max_holding_mins ? `/${Math.floor(p.max_holding_mins / 60)}h` : ""}
                      </td>
                      <td style={td}>
                        {p.be_locked
                          ? <span style={{ color: "#4ade80", fontWeight: 600 }} title="SL at breakeven or better — trade cannot net-lose">🔒 BE+</span>
                          : <span style={{ color: "#64748b" }}>at risk</span>}
                      </td>
                      <td style={td}>{p.tranche_id || p.comment?.replace("Sentinel_", "") || "—"}</td>
                      <td style={{ ...td, color: "#475569", fontSize: "10px" }}>{p.ticket}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ marginTop: "10px", fontSize: "12px", color: "#64748b" }}>
            Total P&L:{" "}
            <span style={{ color: totalPnl >= 0 ? "#4ade80" : "#f87171", fontWeight: 600 }}>
              {totalPnl >= 0 ? "+" : ""}${totalPnl.toFixed(2)}
            </span>
            {account.margin_level > 0 && (
              <span style={{ marginLeft: "16px" }}>
                Margin Level: <span style={{ color: "#60a5fa" }}>{account.margin_level?.toFixed(0)}%</span>
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}
